import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styles from './RateNowModal.module.scss'
import TextArea from '../../atoms/textarea/TextArea'
import { toastHelper } from '../../../util/toastHelper'
import CommonModal from '../../atoms/modal/CommonModal'
import { Box, Rating, Typography } from '@mui/material'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import { createReview } from '../../../features/reviewSlice'

const RateNowModal = ({ isOpen, handleClose, data }) => {
	const dispatch = useDispatch()
	const [files, setFiles] = useState([])
	const [rating, setRating] = useState(0)
	const [review, setReview] = useState('')
	const [previewFiles, setPreviewFiles] = useState([])

	const handleFileChange = (e) => {
		const selectedFiles = e.target.files

		if (!selectedFiles) return

		const fileArray = Array.from(selectedFiles)

		const previewArray = fileArray.map((file) => ({
			file,
			url: URL.createObjectURL(file),
		}))

		setFiles((prev) => [...prev, ...fileArray])
		setPreviewFiles((prev) => [...prev, ...previewArray])

		e.target.value = null
	}

	const handleRemoveFile = (index) => {
		setFiles((prev) => prev.filter((_, i) => i !== index))

		setPreviewFiles((prev) => {
			const updated = [...prev]
			URL.revokeObjectURL(updated[index].url)
			updated.splice(index, 1)
			return updated
		})
	}

	useEffect(() => {
		return () =>
			previewFiles.forEach((file) => URL.revokeObjectURL(file.url))
	}, [previewFiles])

	useEffect(() => {
		if (isOpen) {
			setFiles([])
			setRating(0)
			setReview('')
			setPreviewFiles([])
		}
	}, [isOpen])

	const handleSubmitReview = async () => {
		try {
			if (!data) return

			if (rating === 0)
				return toastHelper('Please select a rating', 'error')

			if (review.trim().length < 10)
				return toastHelper(
					'Review must be at least 10 characters',
					'error'
				)

			const reviewData = {
				rating,
				files,
				proposalId: data.id,
				kazooId: data.kazooId,
				comment: review.trim(),
				vendorId: data.vendorId,
			}

			await dispatch(createReview(reviewData)).unwrap()

			handleClose()
		} catch (error) {
			console.error('Error submitting review:', error)
		}
	}

	if (!isOpen || !data) return null
	return (
		<CommonModal
			open={isOpen}
			handleClose={handleClose}
			heading="Rate Now"
			subHeading=""
			footerContent={
				<>
					<ButtonCustom
						onClick={handleClose}
						variant="outlined"
						size="lg"
					>
						Cancel
					</ButtonCustom>
					<ButtonCustom
						size="lg"
						variant="default"
						onClick={handleSubmitReview}
					>
						Submit
					</ButtonCustom>
				</>
			}
		>
			<form className={styles.rateNowForm} onSubmit={handleSubmitReview}>
				<Box className={styles.cardContainer}>
					<Box className={styles.cardContent}>
						<Box className={styles.cardImageWrapper}>
							<Box
								component="img"
								src={
									data?.businessProfilePic ||
									'/images/ontimebride_iconset_blue_vendor_default.png'
								}
								alt="Churros & Chocolate Stall"
								className={styles.churrosImage}
							/>
						</Box>

						{/* Right Side: Information */}
						<Box className={styles.cardInfoWrapper}>
							<Typography className={styles.cardCategory}>
								{[
									data?.businessType,
									data?.businessTypes?.join(', '),
									data?.customBusinessType,
								]
									.filter(Boolean)
									.join(', ') || ''}
							</Typography>

							<Typography
								variant="h5"
								className={styles.cardTitle}
							>
								{data.vendorName ?? ''}
							</Typography>

							<Box className={styles.cardLocationBox}>
								<img
									alt="location"
									src="/images/location.svg"
									className={styles.churrosLocationIcon}
								/>
								<Typography className={styles.cardAddress}>
									{data?.businessAddress ?? ''}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
				<Box
					sx={{ '& > legend': { mt: 2 } }}
					className={styles.ratingBox}
				>
					<Rating
						name="customized-10"
						max={5}
						value={rating}
						onChange={(e, newValue) => setRating(newValue)}
						icon={
							<img
								src="/images/star.svg"
								alt="filled icon"
								style={{ width: '28px', height: '28px' }}
							/>
						}
						emptyIcon={
							<img
								src="/images/star-without-fill.svg"
								alt="empty icon"
								style={{ width: '28px', height: '28px' }}
							/>
						}
					/>
				</Box>
				<div className={styles.formControl}>
					<div className={styles.formItem}>
						<label htmlFor="review" className={styles.reviewLabel}>
							Write your review
						</label>
						<TextArea
							placeholder="Write something..."
							value={review}
							onChange={(e) => setReview(e.target.value)}
							sx={{ minHeight: '80px', maxHeight: '80px' }}
						/>
					</div>
					<div className={styles.formItem}>
						<div className={styles.uploadContainer}>
							{previewFiles?.length < 3 && (
								<label className={styles.uploadBox}>
									<input
										type="file"
										className={styles.hiddenInput}
										onChange={handleFileChange}
										multiple
									/>
									<div className={styles.content}>
										<img
											src="/images/UploadLogo.svg"
											alt="upload"
										/>
										<span>Upload photos</span>
									</div>
								</label>
							)}

							{/* The List of Previewed Images */}
							{previewFiles?.map((item, index) => (
								<div
									key={index}
									className={styles.imagePreview}
								>
									<img
										src={item?.url}
										alt="Uploaded preview"
									/>
									<div
										className={styles.closeIconWrapper}
										onClick={() => handleRemoveFile(index)}
									>
										<img
											src="/images/modal-close-icon.svg"
											alt="close"
											className={styles.closeIcon}
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</form>
		</CommonModal>
	)
}

export default RateNowModal
